import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { hasSession } from '../lib/auth-client.js'
import { env } from '../lib/env.js'
import { Icon, type IconName } from '../components/Icon.js'
import { MarketingNav } from '../components/MarketingNav.js'
import { MarketingFooter } from '../components/MarketingPage.js'

export const Route = createFileRoute('/')({
  beforeLoad: async () => {
    // Authenticated visitors skip the marketing site and land on the app.
    if (await hasSession()) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: LandingPage,
})

interface PlanRow {
  id: string
  name: string
  description: string | null
  pricingModel: 'pay_as_you_go' | 'monthly' | 'annual'
  priceInr: number
  studentLimit: number | null
  trialDays: number
  features: Record<string, boolean>
}

const FEATURE_DETAILS: { key: string; label: string }[] = [
  { key: 'fee_collection', label: 'Fee collection' },
  { key: 'attendance', label: 'Attendance' },
  { key: 'performance', label: 'Performance' },
  { key: 'exam_boards', label: 'Exam boards' },
  { key: 'parent_communication', label: 'Parent broadcasts' },
  { key: 'referrals', label: 'Referrals' },
  { key: 'white_label', label: 'White label + custom domain' },
  { key: 'social_login', label: 'Google sign-in' },
  { key: 'teacher_accounts', label: 'Teacher accounts' },
  { key: 'email_customisation', label: 'Custom email sender' },
  { key: 'reporting', label: 'Reports' },
  { key: 'whatsapp_notifications', label: 'WhatsApp notifications' },
  { key: 'biometric_sync', label: 'Biometric attendance sync' },
]

function LandingPage() {
  const navigate = useNavigate()
  const plans = useQuery({
    queryKey: ['public', 'plans'],
    queryFn: async () => {
      const res = await fetch(`${env.API_URL}/api/v1/plans`)
      if (!res.ok) throw new Error('Failed to load plans')
      const json = (await res.json()) as { data: PlanRow[] }
      return json.data
    },
  })

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <MarketingNav />
      <main className="flex-1">
        <Hero onPrimary={() => navigate({ to: '/login' })} />
        <Stats />
        <Features />
        <Pricing plans={plans.data ?? []} loading={plans.isLoading} />
        <FinalCta onPrimary={() => navigate({ to: '/login' })} />
      </main>
      <MarketingFooter />
    </div>
  )
}

// ============================================================
// Decorative inline SVG. Used as accent flourishes in the hero
// ============================================================
function StarMark({
  size = 16,
  className = '',
}: {
  size?: number
  className?: string
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
    >
      <path
        d="M12 2.5 L14.94 8.46 L21.5 9.42 L16.75 14.05 L17.88 20.58 L12 17.5 L6.12 20.58 L7.25 14.05 L2.5 9.42 L9.06 8.46 Z"
        fill="currentColor"
      />
    </svg>
  )
}

// ============================================================
// Hero
// ============================================================
function Hero({ onPrimary }: { onPrimary: () => void }) {
  return (
    <section
      className="relative overflow-hidden px-6 py-24 md:py-32"
      style={{
        background:
          'linear-gradient(180deg, #FAF6EF 0%, #F4F0E8 60%, #F0FDFA 100%)',
      }}
    >
      {/* Decorative star accents (kept low-opacity so they read as texture, not content) */}
      <StarMark
        size={28}
        className="absolute top-20 left-[12%] text-[var(--color-primary)] opacity-15"
      />
      <StarMark
        size={20}
        className="absolute top-40 right-[14%] text-warning opacity-30"
      />
      <StarMark
        size={36}
        className="absolute bottom-24 left-[20%] text-[var(--color-primary)] opacity-10"
      />
      <StarMark
        size={16}
        className="absolute top-1/3 right-[26%] text-warning opacity-25"
      />
      <StarMark
        size={22}
        className="absolute bottom-32 right-[12%] text-[var(--color-primary)] opacity-20"
      />

      <div className="relative max-w-3xl mx-auto text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface/80 backdrop-blur px-3 py-1 text-xs font-medium text-fg-muted shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary)] animate-pulse" />
          Built for coaching academies
        </span>
        <h1 className="mt-6 text-[2.5rem] md:text-6xl font-semibold tracking-tight text-fg leading-[1.05]">
          Run your academy
          <span className="block text-[var(--color-primary)]">
            without the spreadsheets.
          </span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-fg-muted max-w-2xl mx-auto leading-relaxed">
          Brightstars brings fees, attendance, tests, parent communication and
          billing into one place. Set up in a day, deliver from day one.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={onPrimary}
            className="group inline-flex items-center justify-center h-12 px-7 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] shadow-sm hover:shadow-md transition-all duration-200 w-full sm:w-auto"
          >
            Sign in
            <Icon
              name="chevronRight"
              size={14}
              className="ml-1.5 transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </button>
          <a
            href="#features"
            className="inline-flex items-center justify-center h-12 px-7 rounded-md border border-border bg-surface text-fg text-sm font-medium hover:bg-surface-muted hover:border-border-strong transition-all duration-200 w-full sm:w-auto"
          >
            See features
          </a>
        </div>
        <p className="mt-5 text-xs text-fg-subtle">
          New academies join by invitation · contact us for access
        </p>
      </div>
    </section>
  )
}

// ============================================================
// Stats strip. Narrow band between hero and features
// ============================================================
const STATS: { label: string; value: string }[] = [
  { label: 'Channels covered', value: 'WhatsApp · Email' },
  { label: 'Setup time', value: 'Under a day' },
  { label: 'Free trial', value: '30 days, no card' },
]

function Stats() {
  return (
    <section className="border-y border-border bg-surface">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
        {STATS.map((stat) => (
          <div key={stat.label} className="py-6 px-4 text-center">
            <p className="text-xs uppercase tracking-wider text-fg-subtle font-medium">
              {stat.label}
            </p>
            <p className="mt-1 text-sm font-semibold text-fg">{stat.value}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

// ============================================================
// Features
// ============================================================
const PRIMARY_FEATURES: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'wallet',
    title: 'Fee collection on Razorpay',
    body: 'Raise invoices in seconds. Parents pay via UPI, cards, or netbanking. Receipts go out automatically.',
  },
  {
    icon: 'calendar',
    title: 'Attendance in 30 seconds',
    body: 'One tap per student. Absent-day WhatsApp to parents in under a minute.',
  },
  {
    icon: 'clipboardCheck',
    title: 'Tests + exam calendar',
    body: 'Enter marks once. Parents and teachers see scores, board exams, and prep status.',
  },
  {
    icon: 'users',
    title: 'Parent portal, free',
    body: 'Every parent gets a login. Children, fees, attendance, marks. One screen, one account across siblings.',
  },
  {
    icon: 'inbox',
    title: 'Targeted broadcasts',
    body: 'Send WhatsApp + email to all parents or just one class. Templates, audit log, delivery receipts.',
  },
  {
    icon: 'settings',
    title: 'Your brand, your domain',
    body: 'Bring your logo, your colours, your domain. Parents see your academy, never us.',
  },
]

function Features() {
  return (
    <section id="features" className="px-6 py-24 bg-surface">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-14">
          <p className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[var(--color-primary)]">
            <StarMark size={12} className="text-[var(--color-primary)]" />
            What you get
          </p>
          <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-fg leading-tight">
            Every common task, simplified.
          </h2>
          <p className="mt-4 text-fg-muted text-lg">
            We didn&apos;t bolt on features. We built the things you actually do
            every day, and removed the rest.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PRIMARY_FEATURES.map((f) => (
            <div
              key={f.title}
              className="group rounded-xl border border-border bg-surface p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-border-strong transition-all duration-200"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--color-primary-tint)] text-[var(--color-primary)] group-hover:scale-105 transition-transform duration-200">
                <Icon name={f.icon} size={20} />
              </span>
              <h3 className="mt-5 text-base font-semibold text-fg">{f.title}</h3>
              <p className="mt-2 text-sm text-fg-muted leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================================
// Pricing
// ============================================================
function Pricing({
  plans,
  loading,
}: {
  plans: PlanRow[]
  loading: boolean
}) {
  return (
    <section
      id="pricing"
      className="relative px-6 py-24"
      style={{ background: 'linear-gradient(180deg, #F0FDFA 0%, #FAF6EF 100%)' }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-14 text-center mx-auto">
          <p className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[var(--color-primary)]">
            <StarMark size={12} className="text-[var(--color-primary)]" />
            Pricing
          </p>
          <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-fg leading-tight">
            Pick a plan that fits your academy.
          </h2>
          <p className="mt-4 text-fg-muted text-lg">
            Every plan starts with a free trial. No setup fees. Cancel any time.
          </p>
          <p className="mt-2 text-xs text-fg-subtle">
            All prices are in Indian Rupees (INR). GST is charged at the
            applicable rate (currently 18%) in addition to the displayed plan
            price. See our{' '}
            <a
              href="/refund"
              className="text-[var(--color-primary)] hover:underline"
            >
              refund &amp; cancellation policy
            </a>{' '}
            for details.
          </p>
        </div>

        {loading ? (
          <p className="text-fg-subtle text-sm text-center">Loading plans…</p>
        ) : plans.length === 0 ? (
          <div className="max-w-2xl mx-auto rounded-xl border border-border bg-surface/80 backdrop-blur px-6 py-8 text-center shadow-sm">
            <p className="text-sm text-fg-muted">
              Plans haven&apos;t been published yet. Contact us for early access.
            </p>
            <a
              href="mailto:hello@brightstars.in"
              className="mt-4 inline-flex items-center text-sm font-medium text-[var(--color-primary)] hover:underline"
            >
              hello@brightstars.in
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {plans.map((plan, i) => (
              <PlanCard key={plan.id} plan={plan} highlighted={i === 1} />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

function PlanCard({
  plan,
  highlighted,
}: {
  plan: PlanRow
  highlighted: boolean
}) {
  const rupees = (plan.priceInr / 100).toLocaleString('en-IN')
  const billingLabel =
    plan.pricingModel === 'monthly'
      ? '/month'
      : plan.pricingModel === 'annual'
        ? '/year'
        : ''
  const enabledFeatures = FEATURE_DETAILS.filter((f) => plan.features[f.key])

  return (
    <div
      className={`relative rounded-xl bg-surface p-7 transition-all duration-200 ${
        highlighted
          ? 'border-2 border-[var(--color-primary)] shadow-md md:-translate-y-2 hover:shadow-lg'
          : 'border border-border shadow-sm hover:shadow-md hover:-translate-y-0.5'
      }`}
    >
      {highlighted ? (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-[var(--color-primary)] text-white px-3 py-1 text-xs font-semibold shadow-sm">
          <StarMark size={10} className="text-white" />
          Most popular
        </span>
      ) : null}
      <h3 className="text-lg font-semibold text-fg">{plan.name}</h3>
      {plan.description ? (
        <p className="mt-1.5 text-sm text-fg-muted">{plan.description}</p>
      ) : null}
      <div className="mt-6 flex items-baseline gap-1">
        <span className="text-4xl font-semibold text-fg tabular-nums">
          ₹{rupees}
        </span>
        <span className="text-sm text-fg-subtle">{billingLabel}</span>
      </div>
      <p className="mt-1 text-xs text-fg-subtle">
        {plan.trialDays}-day free trial
        {plan.studentLimit ? ` · up to ${plan.studentLimit} students` : ''}
      </p>
      <a
        href="/login"
        className={`mt-6 inline-flex items-center justify-center w-full h-11 rounded-md text-sm font-semibold transition-all duration-200 ${
          highlighted
            ? 'bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] shadow-sm hover:shadow-md'
            : 'border border-border bg-surface text-fg hover:bg-surface-muted hover:border-border-strong'
        }`}
      >
        Sign in
      </a>
      {enabledFeatures.length > 0 ? (
        <ul className="mt-6 space-y-2.5 pt-6 border-t border-border">
          {enabledFeatures.slice(0, 8).map((f) => (
            <li key={f.key} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-success-tint text-success shrink-0">
                <Icon name="check" size={10} />
              </span>
              <span className="text-fg-muted">{f.label}</span>
            </li>
          ))}
          {enabledFeatures.length > 8 ? (
            <li className="text-xs text-fg-subtle pl-6">
              +{enabledFeatures.length - 8} more
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}

// ============================================================
// Final CTA, deeper tint, more visual weight
// ============================================================
function FinalCta({ onPrimary }: { onPrimary: () => void }) {
  return (
    <section
      id="faq"
      className="relative overflow-hidden px-6 py-24"
      style={{ background: 'linear-gradient(180deg, #FAF6EF 0%, #E0F2EF 100%)' }}
    >
      <StarMark
        size={120}
        className="absolute -top-6 -right-8 text-[var(--color-primary)] opacity-5 rotate-12"
      />
      <StarMark
        size={80}
        className="absolute bottom-8 -left-4 text-[var(--color-primary)] opacity-10 -rotate-12"
      />
      <div className="relative max-w-3xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-fg leading-tight">
          Get your academy running by tomorrow.
        </h2>
        <p className="mt-4 text-lg text-fg-muted">
          Sign up in a minute. Add your first student in five. Send your first
          fee invoice before lunch.
        </p>
        <button
          type="button"
          onClick={onPrimary}
          className="group mt-8 inline-flex items-center h-12 px-8 rounded-md bg-[var(--color-primary)] text-white text-sm font-semibold hover:bg-[var(--color-primary-hover)] shadow-sm hover:shadow-md transition-all duration-200"
        >
          Sign in
          <Icon
            name="chevronRight"
            size={14}
            className="ml-1.5 transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </button>
        <p className="mt-5 text-xs text-fg-subtle">
          Have questions first?{' '}
          <a
            href="mailto:hello@brightstars.in"
            className="text-[var(--color-primary)] hover:underline"
          >
            hello@brightstars.in
          </a>
        </p>
      </div>
    </section>
  )
}

